import {
    collection,
    CollectionReference,
    getDocs,
    getDoc,
    query,
    doc,
    updateDoc,
    DocumentData,
} from "firebase/firestore";
import FirebaseService from "./firebaseService";
import {IDateCardOrder} from "../types/DateCardOrders";
import { DateCardOrderStatus } from "../constants/DateCardOrderStatus";
import { NotificationType } from "../constants/NotificationType";
import { sendUserNotification } from "../utils/notificationUtils";
import { DigitalCardReviewStatus } from "../constants/DigitalCardReviewStatus";


class DateCardsService {
    ref: CollectionReference;

    constructor() {
        const db = FirebaseService.db;
        this.ref = collection(db, 'DateCardOrders');
    }

    async getDateCardOrders(): Promise<IDateCardOrder[]> {
        const q = query(this.ref);
        try {
            const docs = await getDocs(q);
           
            const orders = docs.docs.map(async (dateCardDoc) => {
                const data = dateCardDoc.data();
                return this._toOrders(data, dateCardDoc.id);
            });
            return (await Promise.all(orders)).flat();
          } catch (error) {
            console.error("Error fetching documents: ", error);
          }
          return (await Promise.all([]));
    }

    async getDeliveredDateCardOrders(): Promise<IDateCardOrder[]> {
        const q = query(this.ref);
        try {
            const docs = await getDocs(q);

            const orders = docs.docs.map(async (dateCardDoc) => {
                const data = dateCardDoc.data();
                const allOrders = this._toOrders(data, dateCardDoc.id);
    
                // Filter orders to only include those with the 'delivered' status
                return allOrders.filter(order => order.orderStatus === DateCardOrderStatus.delivered);
            });
    
            return (await Promise.all(orders)).flat();
        } catch (error) {
            console.error("Error fetching delivered orders: ", error);
        }
        return (await Promise.all([]));
    }

    async getPendingDateCardOrders(): Promise<IDateCardOrder[]> {
        const q = query(this.ref);
        try {
            const docs = await getDocs(q);
    
            const orders = docs.docs.map(async (dateCardDoc) => {
                const data = dateCardDoc.data();
                const allOrders = this._toOrders(data, dateCardDoc.id);

                // Filter orders to only include those with the 'delivered' status
                return allOrders.filter(order => ![undefined, null, DateCardOrderStatus.default, DateCardOrderStatus.delivered].includes(order.orderStatus as DateCardOrderStatus | undefined | null));
            });
    
            return (await Promise.all(orders)).flat();
        } catch (error) {
            console.error("Error fetching delivered orders: ", error);
        }
        return (await Promise.all([]));
    }

    async updateOrderStatus(orderId: string, dateCardDocId: string, newStatus: string) {
        const orderDocRef = doc(this.ref, dateCardDocId);
        console.log(newStatus);
        const dateCardDoc = await getDoc(orderDocRef);
        if (!dateCardDoc.exists()) {
            throw new Error("Document not found");
        }

        const orders = dateCardDoc.data()?.Orders || [];
        // Find the order by orderId and update its status
        const orderIndex = orders.findIndex((order: any) => order.orderID === orderId);
        if (orderIndex === -1) {
            throw new Error("Order not found");
        }

        orders[orderIndex].orderStatus = newStatus;

        const isPendingReason = newStatus !== '' && newStatus !== null && newStatus !== DateCardOrderStatus.delivered;
        if (newStatus === DateCardOrderStatus.delivered) {
            orders[orderIndex].processedAt = new Date().toISOString();
        } else if (isPendingReason) {
            orders[orderIndex].markedPendingAt = new Date().toISOString();
        }

        // Update the entire orders array in Firestore. This dropdown is purely for later
        // fulfillment/mailing tracking — no notification here. Content-review notifications
        // (approve / request changes) are handled separately by approveOrderContent() and
        // requestOrderContentChanges() below, before an order ever reaches this stage.
        await updateDoc(orderDocRef, { Orders: orders });
    }

    /**
     * New orders (orderStatus unset/default, i.e. not yet in the fulfillment pipeline at all)
     * that also haven't been through content review yet (contentReviewStatus unset or
     * 'pending-review'). NOTE: currently unused by any admin screen — the "Date Card Review"
     * tab (DigitalDateCardsList.tsx) is digital-card submissions only; in-person/physical
     * orders are reviewed via the "New Date Card Orders" tab's own status dropdown instead.
     * Kept in case order-level content review gets a home again.
     */
    async getPendingContentReviewOrders(): Promise<IDateCardOrder[]> {
        const orders = await this.getDateCardOrders();
        return orders.filter(order =>
            (order.orderStatus === undefined || order.orderStatus === null || order.orderStatus === DateCardOrderStatus.default) &&
            (order.contentReviewStatus === undefined || order.contentReviewStatus === DigitalCardReviewStatus.pendingReview)
        );
    }

    async approveOrderContent(orderId: string, dateCardDocId: string): Promise<void> {
        const orderDocRef = doc(this.ref, dateCardDocId);
        const dateCardDoc = await getDoc(orderDocRef);
        if (!dateCardDoc.exists()) {
            throw new Error("Document not found");
        }

        const orders = dateCardDoc.data()?.Orders || [];
        const orderIndex = orders.findIndex((order: any) => order.orderID === orderId);
        if (orderIndex === -1) {
            throw new Error("Order not found");
        }

        orders[orderIndex].contentReviewStatus = DigitalCardReviewStatus.approved;
        orders[orderIndex].contentApprovedAt = new Date().toISOString();

        await updateDoc(orderDocRef, { Orders: orders });

        void sendUserNotification(dateCardDocId, {
            title: 'Your card is approved 🎉',
            message: 'Your Date Card has been approved and is ready to use.',
            data: { type: NotificationType.DATE_CARD_APPROVED },
        });
    }

    async requestOrderContentChanges(orderId: string, dateCardDocId: string, note: string): Promise<void> {
        const orderDocRef = doc(this.ref, dateCardDocId);
        const dateCardDoc = await getDoc(orderDocRef);
        if (!dateCardDoc.exists()) {
            throw new Error("Document not found");
        }

        const orders = dateCardDoc.data()?.Orders || [];
        const orderIndex = orders.findIndex((order: any) => order.orderID === orderId);
        if (orderIndex === -1) {
            throw new Error("Order not found");
        }

        orders[orderIndex].contentReviewStatus = DigitalCardReviewStatus.changesRequested;
        orders[orderIndex].contentReviewNote = note;
        orders[orderIndex].contentReviewRequestedAt = new Date().toISOString();

        await updateDoc(orderDocRef, { Orders: orders });

        void sendUserNotification(dateCardDocId, {
            title: "We couldn't approve your Date Card",
            message: 'Open Skip to review what needs to be updated.',
            data: { type: NotificationType.DATE_CARD_CHANGES_REQUESTED },
        });
    }

    /**
     * Converts a DateCardOrders/{uid} document's data into a flat list of IDateCardOrder rows.
     *
     * A document with no valid "Orders" array is NOT necessarily malformed: documents that
     * instead carry a "DigitalCard" map (handled separately by digitalDateCardService.ts) are
     * an expected, common shape and are silently skipped here (0 orders, no warning). Only a
     * document with neither a valid "Orders" array nor a "DigitalCard" field is genuinely
     * unexpected, and still warns.
     */
    private _toOrders(data: DocumentData, dateCardDocId: string): IDateCardOrder[] {
        const snapshots = data["Orders"];
        if (!Array.isArray(snapshots)) {
            if (!data["DigitalCard"]) {
                console.warn(
                    `dateCardsService: DateCardOrders/${dateCardDocId} has no valid "Orders" array ` +
                    `(got ${typeof snapshots}) and no "DigitalCard" field; treating as 0 orders.`
                );
            }
            return [];
        }
        return snapshots.map((doc: { data: () => IDateCardOrder; id: any; }) => {
            return { ...doc, dateCardDocId: dateCardDocId } as unknown as IDateCardOrder;
        });
    }

}

export default new DateCardsService();